import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// Mailer Setup → "Red flag warnings".
//
// The other tab of this page schedules report mails to typed-in addresses. This one sets
// up mails the system sends by itself when something happens to a person: as soon as an
// employee receives a red flag, they get the message written for the red-flag number they
// have just reached (2nd, 3rd, 4th, 5th…), with the details of why added by the system,
// and every mail is kept in a trail.
//
// Top to bottom: whether the mails are on (off for every project until someone adds a
// message and switches them on), the messages, who is copied, and the trail. Everything
// on screen comes from one `config` answer; after any change it is read again rather than
// patched here, because what a message is "sent for" and which numbers no message covers
// are worked out by the server from all the messages together.
//
// Access is Red Flag Rules access for the selected project, probed first. Mailer Setup
// itself is only a menu entry, so a person can reach this tab without it — they get a
// plain panel saying what to ask for, not an error. A person the server could not
// recognise is told to sign in again instead: asking a super admin would not help them.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Plus } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import {
  redFlagWarningMailsAPI,
  type RedFlagWarningConfig,
  type RedFlagWarningStep,
} from '../../services/api';
import TableCardWithStates from '../TableCardWithStates';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { PageErrorBanner } from '../PageErrorBanner';
import { NoProjectSelectedState } from '../NoProjectSelectedState';
import { InlineLoader } from '../loading';
import { WarningStatusCard } from './WarningStatusCard';
import { WarningDelivery } from './WarningDelivery';
import { WarningMessageModal } from './WarningMessageModal';
import { WarningMailTrail } from './WarningMailTrail';
import { WarningPreviewModal, WarningTestSendModal } from './WarningPreview';
import { isUnverifiedRefusal } from '../red-flag-rules/ruleModel';
import { apiMessage, flagNumberLabel, httpStatus, ordinal } from './warningMailText';

type Access = 'checking' | 'granted' | 'denied' | 'unverified' | 'error';

const NO_ACCESS_MESSAGE = 'You need Red Flag Rules access for this project. Ask a super admin.';

// The access check could not verify who is asking (a stale session, a sign-in service
// hiccup) — asking for access would be the wrong advice. Same words as Red Flag Rules,
// whose access this is.
const UNVERIFIED_MESSAGE =
  'We couldn’t verify your identity for Red Flag Rules. Please sign in again.';

// Shown whenever the re-read after a change fails. The change itself went through, so
// the re-read's own error ("Something went wrong…") would say the opposite of what happened.
const SAVED_NOT_REFRESHED =
  'Your change was saved, but the page could not be refreshed. Reload to see it.';

const RetryButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
  >
    Try again
  </button>
);

export default function RedFlagWarningsTab({ actionSlot }: { actionSlot?: HTMLElement | null }) {
  const projectId = useAuthStore((s) => s.selectedProjectId);

  const [access, setAccess] = useState<Access>('checking');
  const [accessError, setAccessError] = useState<string | null>(null);
  const [config, setConfig] = useState<RedFlagWarningConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  // Message dialog: `false` = closed, `null` = a new message, a step = editing it.
  const [editing, setEditing] = useState<RedFlagWarningStep | null | false>(false);
  const [previewStep, setPreviewStep] = useState<RedFlagWarningStep | null>(null);
  const [testStep, setTestStep] = useState<RedFlagWarningStep | null>(null);
  const [deleteStep, setDeleteStep] = useState<RedFlagWarningStep | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Drops an answer that belongs to a project the user has since left.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setAccess('checking');
    setAccessError(null);
    setConfig(null);
    setLoadError(null);
    setMessagesError(null);
    setEditing(false);
    setPreviewStep(null);
    setTestStep(null);
    setDeleteStep(null);
    if (!projectId) return;
    try {
      const res = await redFlagWarningMailsAPI.access(projectId);
      if (mine !== seq.current) return;
      if (res.data?.data?.canManage !== true) {
        setAccess(res.data?.data?.reason === 'unverified' ? 'unverified' : 'denied');
        return;
      }
    } catch (e) {
      if (mine !== seq.current) return;
      const status = httpStatus(e);
      if (status === 401 || status === 403) {
        setAccess(isUnverifiedRefusal(e) ? 'unverified' : 'denied');
        return;
      }
      setAccess('error');
      setAccessError(status === 404
        ? 'Red flag warning mails aren’t available on this server yet.'
        : apiMessage(e, 'Could not check your access to red flag warning mails.'));
      return;
    }
    setAccess('granted');
    try {
      const res = await redFlagWarningMailsAPI.getConfig(projectId);
      if (mine !== seq.current) return;
      setConfig(res.data.data);
    } catch (e) {
      if (mine !== seq.current) return;
      if (httpStatus(e) === 403) {
        // The refusal says which it is: not recognised (sign in again) or no access.
        setAccess(isUnverifiedRefusal(e) ? 'unverified' : 'denied');
        return;
      }
      setLoadError(apiMessage(e, 'Could not load the red flag warning mails.'));
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Read everything again after a change, keeping what is on screen until the answer is in. */
  const refresh = useCallback(async () => {
    if (!projectId) return;
    const mine = seq.current;
    try {
      const res = await redFlagWarningMailsAPI.getConfig(projectId);
      if (mine === seq.current) setConfig(res.data.data);
    } catch {
      if (mine === seq.current) setMessagesError(SAVED_NOT_REFRESHED);
    }
  }, [projectId]);

  const toggleStep = async (step: RedFlagWarningStep) => {
    if (!projectId) return;
    const mine = seq.current;
    const verb = step.isActive ? 'pause' : 'resume';
    setTogglingId(step.id);
    setMessagesError(null);
    try {
      // The save replaces the whole message, and this page may have been open a while.
      // Read the message again first, so wording someone else saved meanwhile is not put back.
      const latest = (await redFlagWarningMailsAPI.getConfig(projectId)).data.data;
      if (mine !== seq.current) return;
      const fresh = latest.steps.find((s) => s.id === step.id);
      if (!fresh) {
        setConfig(latest);
        setMessagesError(`That message has been deleted, so there is nothing to ${verb}.`);
        return;
      }
      await redFlagWarningMailsAPI.updateStep(projectId, step.id, {
        flagNumber: fresh.flagNumber,
        subjectTemplate: fresh.subjectTemplate,
        bodyTemplate: fresh.bodyTemplate,
        andLater: fresh.andLater,
        // What the button said it would do, whatever the message's switch is by now.
        isActive: !step.isActive,
      });
      await refresh();
    } catch (e) {
      setMessagesError(apiMessage(e, `Could not ${verb} the message. Please try again.`));
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!projectId || !deleteStep) return;
    setDeleting(true);
    setMessagesError(null);
    try {
      await redFlagWarningMailsAPI.deleteStep(projectId, deleteStep.id);
      setDeleteStep(null);
      await refresh();
    } catch (e) {
      // The dialog has no room for an error; close it and say why above the table.
      setDeleteStep(null);
      setMessagesError(apiMessage(e, 'Could not delete the message. Please try again.'));
    } finally {
      setDeleting(false);
    }
  };

  if (!projectId) {
    return <NoProjectSelectedState message="Select a project from the dropdown in the menu bar to set up its red flag warning mails." />;
  }

  if (access === 'denied' || access === 'unverified') {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center" role="status">
        <Lock className="mx-auto h-10 w-10 text-gray-300" aria-hidden />
        <p className="mx-auto mt-4 max-w-md text-sm text-gray-600">
          {access === 'unverified' ? UNVERIFIED_MESSAGE : NO_ACCESS_MESSAGE}
        </p>
      </div>
    );
  }

  if (access === 'error') {
    return (
      <div className="space-y-3">
        <PageErrorBanner message={accessError ?? 'Could not check your access to red flag warning mails.'} />
        <RetryButton onClick={() => void load()} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <PageErrorBanner message={loadError} />
        <RetryButton onClick={() => void load()} />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white" aria-busy="true">
        <InlineLoader label="Loading..." className="flex justify-center p-12" />
      </div>
    );
  }

  const steps = [...config.steps].sort((a, b) => a.flagNumber - b.flagNumber);

  const addButton = (
    <button
      type="button"
      onClick={() => setEditing(null)}
      className="inline-flex items-center gap-2 btn-brand text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
    >
      <Plus className="w-4 h-4" /> Add message
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Inside the Mailer Setup shell the button sits in the tab row; on its own it keeps a row. */}
      {actionSlot ? createPortal(addButton, actionSlot) : actionSlot === undefined && (
        <div className="flex justify-end">{addButton}</div>
      )}

      <WarningStatusCard projectId={projectId} config={config} onChanged={refresh} />

      <section aria-labelledby="rfw-messages-title" className="space-y-3">
        <div>
          <h2 id="rfw-messages-title" className="text-lg font-semibold text-gray-900">Messages</h2>
          <p className="text-sm text-gray-500">
            One message for each red flag number. An employee gets the message for the number they have just reached.
          </p>
        </div>

        {messagesError && <PageErrorBanner message={messagesError} />}

        <TableCardWithStates
          loading={false}
          isEmpty={steps.length === 0}
          emptyMessage="No messages yet. Add a message for each red flag number that should get a mail."
        >
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Red flag</th>
                <th className="text-left px-4 py-3 font-medium">Subject</th>
                <th className="text-left px-4 py-3 font-medium">Sent for</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-center px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {steps.map((step) => (
                <tr key={step.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{ordinal(step.flagNumber)}</td>
                  <td className="px-4 py-3 text-gray-700">{step.subjectTemplate}</td>
                  <td className="px-4 py-3 text-gray-700">{step.sentFor}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${
                        step.isActive
                          ? 'border-green-200 bg-green-50 text-green-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}
                    >
                      {step.isActive ? 'On' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <TableActionGroup>
                      <TableActionButton action={step.isActive ? 'pause' : 'activate'} label={step.isActive ? 'Pause' : 'Resume'} onClick={() => void toggleStep(step)} disabled={togglingId === step.id} role="switch" aria-checked={step.isActive} title={step.isActive ? 'Pause' : 'Resume'} />
                      <TableActionButton action="edit" label="Edit" onClick={() => setEditing(step)} title="Edit" />
                      <TableActionButton action="view" label="Preview" onClick={() => setPreviewStep(step)} title="Preview" />
                      <TableActionButton action="send" label="Send test" onClick={() => setTestStep(step)} title="Send test" />
                      <TableActionButton action="delete" label="Delete" onClick={() => setDeleteStep(step)} title="Delete" />
                    </TableActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCardWithStates>

        {config.notCovered && (
          <p className="text-sm text-amber-800">No mail is sent for: {config.notCovered}</p>
        )}
      </section>

      <WarningDelivery
        projectId={projectId}
        settings={config.settings}
        canEdit={config.canReadTrail}
        onSaved={refresh}
      />

      <WarningMailTrail projectId={projectId} canRead={config.canReadTrail} />

      {editing !== false && (
        <WarningMessageModal
          key={editing?.id ?? 'new'}
          projectId={projectId}
          config={config}
          step={editing}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            setMessagesError(null);
            await refresh();
          }}
        />
      )}
      <WarningPreviewModal projectId={projectId} step={previewStep} onClose={() => setPreviewStep(null)} />
      <WarningTestSendModal projectId={projectId} step={testStep} onClose={() => setTestStep(null)} />
      <ConfirmDeleteModal
        isOpen={deleteStep !== null}
        onClose={() => setDeleteStep(null)}
        onConfirm={() => void confirmDelete()}
        isSubmitting={deleting}
        title="Delete message"
        entityLabel="message for the"
        entityName={deleteStep ? flagNumberLabel(deleteStep.flagNumber) : ''}
        consequenceNote="No mail will be sent for that red flag number unless another message covers it. Mails already sent stay in the mail trail."
      />
    </div>
  );
}
